import { Box, Button, Group, Stack, Text } from "@mantine/core";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";
import { useState, useCallback } from "react";
import { useMedplum } from "@medplum/react";
import type { Task } from "@medplum/fhirtypes";

import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { snoozeTask } from "@benchmark/core/actions";
import { toDateInputValue } from "@benchmark/ui/utils";

/**
 * Props for the SnoozeTaskForm component.
 * @agentic connections
 */
type SnoozeTaskFormProps = {
  task: Task;
  onSuccess?: () => void;
  onCancel?: () => void;
};

/**
 * Form for snoozing a task by setting a new due date.
 * @agentic connections
 */
export const SnoozeTaskForm = ({
  task,
  onSuccess,
  onCancel,
}: SnoozeTaskFormProps) => {
  const medplum = useMedplum();
  const [snoozeDate, setSnoozeDate] = useState<string>("");
  const [customSnooze, setCustomSnooze] = useState(false);

  // Track which quick preset is selected to style buttons
  const [selectedPreset, setSelectedPreset] = useState<
    "1" | "2" | "3" | "7" | "custom" | null
  >(null);

  // Helper to add business days (skips weekends)
  const addBusinessDays = useCallback((start: Date, days: number): Date => {
    const result = new Date(start);
    let remaining = days;
    while (remaining > 0) {
      result.setDate(result.getDate() + 1);
      const day = result.getDay();
      if (day !== 0 && day !== 6) {
        remaining--;
      }
    }
    return result;
  }, []);

  const handleQuickSnooze = useCallback(
    (days: number, businessDays = false) => {
      const baseDate = new Date();
      const newDate = businessDays
        ? addBusinessDays(baseDate, days)
        : new Date(baseDate.setDate(baseDate.getDate() + days));
      setSnoozeDate(toDateInputValue(newDate));
      setCustomSnooze(false);
      if (days === 1) {
        setSelectedPreset("1");
      } else if (days === 2) {
        setSelectedPreset("2");
      } else if (days === 3) {
        setSelectedPreset("3");
      } else if (days === 7) {
        setSelectedPreset("7");
      }
    },
    [addBusinessDays],
  );

  const handleSubmit = useCallback(async () => {
    if (!snoozeDate) return;
    try {
      await snoozeTask(medplum, { task, dueDateIso: snoozeDate });
      showSuccessNotification("Task due date updated");
      onSuccess?.();
    } catch (error) {
      console.error("Error snoozing task:", error);
      showErrorNotification(
        "Failed to update task due date. Please try again.",
      );
    }
  }, [medplum, snoozeDate, task, onSuccess]);

  return (
    <Stack gap="md">
      <Text size="sm" c="dimmed" ta="center" mt="sm">
        Set a new due date for this task. The task will remain in your list
        until the new due date.
      </Text>

      {/* Quick action buttons */}
      <Group gap="sm" justify="center" mb="sm">
        <Button
          variant={selectedPreset === "1" ? "filled" : "light"}
          color={selectedPreset === "1" ? "purple" : undefined}
          size="xs"
          onClick={() => handleQuickSnooze(1, true)}
        >
          1 day
        </Button>
        <Button
          variant={selectedPreset === "2" ? "filled" : "light"}
          color={selectedPreset === "2" ? "purple" : undefined}
          size="xs"
          onClick={() => handleQuickSnooze(2, true)}
        >
          2 days
        </Button>
        <Button
          variant={selectedPreset === "3" ? "filled" : "light"}
          color={selectedPreset === "3" ? "purple" : undefined}
          size="xs"
          onClick={() => handleQuickSnooze(3, true)}
        >
          3 days
        </Button>
        <Button
          variant={selectedPreset === "7" ? "filled" : "light"}
          color={selectedPreset === "7" ? "purple" : undefined}
          size="xs"
          onClick={() => handleQuickSnooze(7, false)}
        >
          Next week
        </Button>
        <Button
          variant={customSnooze ? "filled" : "light"}
          color={customSnooze ? "purple" : undefined}
          size="xs"
          onClick={() => {
            setCustomSnooze(true);
            setSelectedPreset("custom");
          }}
        >
          Custom
        </Button>
      </Group>

      {customSnooze && (
        <Box>
          <Text size="sm" fw={500} m="xs">
            New Due Date{" "}
            <Text span c="red">
              *
            </Text>
          </Text>
          {/* TODO(#981): Abstract a shared DateSelect to standardize date UX */}
          <TextInput
            type="date"
            value={snoozeDate}
            onChange={(e) => {
              const value = e.target.value;
              if (value) {
                setSnoozeDate(value);
              } else {
                setSnoozeDate("");
              }
            }}
            min={toDateInputValue(new Date())}
            required
          />
        </Box>
      )}

      <Group justify="flex-end" gap="sm" m="sm">
        <Button variant="outline" onClick={onCancel}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} disabled={!snoozeDate} color="purple">
          Update Due Date
        </Button>
      </Group>
    </Stack>
  );
};
